import React, { createContext, useContext, useEffect, useState } from 'react';

// Theme preference: 'light' | 'dark' | 'system'. Stored per-device in
// localStorage ('system' is the default and stores nothing). The semantic
// color tokens auto-flip when the `dark` class is on <html> — see
// .claude/style-guide.md. A matching no-flash script in public/index.html
// applies the class before React mounts; keep the two in sync.
const STORAGE_KEY = 'theme';

const ThemeContext = createContext({ theme: 'system', setTheme: () => {} });

function systemPrefersDark() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyTheme(theme) {
  const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', dark);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'light' || stored === 'dark' ? stored : 'system';
    } catch {
      return 'system';
    }
  });

  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'system') return;
    // Follow OS-level changes live while in system mode
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = (next) => {
    setThemeState(next);
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage unavailable (private mode) — theme still applies for the session
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
